"use client";

import { getHasAccessToLoggedOutInsiderMarketingPage } from "@faire/deprecated-retailer-visitor-shared/hooks/insider/getHasAccessToLoggedOutInsiderMarketingPage";
import { AccessibilityAnchor } from "@faire/deprecated-retailer-visitor-shared/lib/AccessibilityLink";
import { blogPage } from "@faire/deprecated-retailer-visitor-shared/paths";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { useSettingOpenWithFaireAvailableForCountry } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingOpenWithFaireAvailableForCountry";
import { useSettingOwFGrant } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingOwFGrant";
import { Core } from "@faire/slate/foundation/color";
import { trackHelpCenterFaireHomeFooterHelpCenterClick } from "@faire/web-api--source/events/helpCenter/click/faireHomeFooterHelpCenter";
import { trackLoggedOutRetailersFooterLinksActionCategoryClick } from "@faire/web-api--source/events/loggedOutRetailers/actionCategoryClick/footerLinks";
import { trackLoggedOutRetailersHomePageFooterInsiderLabelClick } from "@faire/web-api--source/events/loggedOutRetailers/click/homePageFooterInsiderLabel";
import type { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import affiliates from "@faire/web-api--source/routes/www/affiliates";
import brands from "@faire/web-api--source/routes/www/brands";
import careers from "@faire/web-api--source/routes/www/careers";
import insiderMarketingPage from "@faire/web-api--source/routes/www/insider";
import faireMarkets from "@faire/web-api--source/routes/www/markets";
import newsroomPage from "@faire/web-api--source/routes/www/news";
import posAbout from "@faire/web-api--source/routes/www/point-of-sale/about";
import support from "@faire/web-api--source/routes/www/support";
import supportRetailers from "@faire/web-api--source/routes/www/support/retailers";
import { SOCIAL } from "@faire/web/common/consts/SOCIAL";
import { useStrictLocalization } from "@faire/web/common/localization";
import { SocialFacebook } from "@faire/web/slate/icons/foundation/SocialFacebook";
import { SocialInstagram } from "@faire/web/slate/icons/foundation/SocialInstagram";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";

import { IFooterLink, SectionLink } from "./SectionLink";
import { SocialX } from "./SocialX";

interface IFooterSection {
  title: string;
  links: IFooterLink[];
}

export const FooterSection: React.FC<{
  locale: string;
  isLoggedInRetailer: boolean;
  retailerOrSessionCountry: keyof typeof ISO3166Alpha3 | undefined;
}> = ({ locale, isLoggedInRetailer, retailerOrSessionCountry }) => {
  const { strictLocalize } = useStrictLocalization();
  const retailerToken = getRetailer()?.token;
  const isEligibleForOwFGrant = useSettingOwFGrant();
  const isOpenWithFaireAvailable = useSettingOpenWithFaireAvailableForCountry();
  const hasAccessToLoggedOutInsiderMarketingPage =
    getHasAccessToLoggedOutInsiderMarketingPage(retailerOrSessionCountry);

  const footerSections: IFooterSection[] = [
    {
      title: strictLocalize({
        id: "emB9+l",
        defaultMessage: "Company",
        description: { text: "group of links to pages related to Faire" },
      }),
      links: [
        {
          name: strictLocalize({
            id: "g1AR2a",
            defaultMessage: "About us",
            description: {
              text: "Label on a button that takes you to the about us page",
              img: "https://cdn.faire.com/fastly/93eef6e47ce10140b20f33493b3b79691426127c350fc9d715ddc68cebaba1a8.png",
            },
          }),
          to: "/about",
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({ id: "8n7nzo", defaultMessage: "Newsroom" }),
          to: newsroomPage(),
          target: undefined,
          isExternal: true,
          shouldShow: true,
        },
        {
          name: strictLocalize({ id: "yJ1ORH", defaultMessage: "Careers" }),
          to: careers(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            id: "PvaPSn",
            defaultMessage: "Affiliates",
            description: {
              text: "Link to the affiliates page",
              img: "https://cdn.faire.com/fastly/c2f423a100c79716eb5efb6b965b654483b9addb7b2c15c02c50d0726f27a16c.png",
            },
          }),
          to: affiliates(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({ id: "tv5FG3", defaultMessage: "Blog" }),
          to: `${blogPage(locale)}/`,
          target: "_blank",
          isExternal: true,
          shouldShow: true,
        },
      ],
    },
    {
      title: strictLocalize({
        id: "nHqI97",
        defaultMessage: "Explore",
        description: {
          text: "Group of Faire resource links to explore",
          img: "https://cdn.faire.com/fastly/527ce0c2c2f77983f781fe71151c77fd50ba2468c4f78d66a03bcc143d4f68f8.png",
        },
      }),
      links: [
        {
          name: strictLocalize({
            id: "02wgPf",
            defaultMessage: "Help center",
            description: {
              text: "Label on a button that takes you to the help center page",
              img: "https://cdn.faire.com/fastly/93eef6e47ce10140b20f33493b3b79691426127c350fc9d715ddc68cebaba1a8.png",
            },
          }),
          to: isLoggedInRetailer ? supportRetailers() : support(),
          target: "_blank",
          isExternal: false,
          shouldShow: true,
          peteEvent: () =>
            trackHelpCenterFaireHomeFooterHelpCenterClick(
              retailerToken ?? "",
              ""
            ),
        },
        {
          name: strictLocalize({
            id: "PVie/y",
            defaultMessage: "Open with Faire",
          }),
          to: "/open-with-faire",
          target: "_blank",
          isExternal: false,
          shouldShow: isOpenWithFaireAvailable,
          eventPath: "/open-with-faire/general-footer/click",
        },
        {
          name: strictLocalize({
            id: "nMCzg9",
            defaultMessage: "Faire Markets",
            description: {
              text: "Link to faire markets page which gives an overview of what faire markets are, and provides links to learn more about past events",
            },
          }),
          to: faireMarkets(),
          target: "_blank",
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            id: "HlqOBy",
            defaultMessage: "Sell on Faire",
            description: {
              text: "Label on a button to apply as a brand",
            },
          }),
          to: brands(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            id: "+mb53H",
            defaultMessage: "Small Business Grant",
            description: {
              text: "Label on a button that takes you to the small business grant page",
            },
          }),
          to: "/small-business-grant",
          target: undefined,
          isExternal: false,
          shouldShow: isEligibleForOwFGrant,
        },
        {
          name: strictLocalize({
            id: "rSKMj1",
            defaultMessage: "Insider",
            description: {
              text: "Label on a button that takes you to the insider marketing page",
              img: "https://cdn.faire.com/fastly/3ce05e0d8114e6dd90a8ef89787032930aa0b07debd8e0c80a954d798bd50aa3.png",
            },
          }),
          to: insiderMarketingPage(),
          target: "_blank",
          isExternal: false,
          shouldShow:
            !isLoggedInRetailer && hasAccessToLoggedOutInsiderMarketingPage,
          peteEvent: () =>
            trackLoggedOutRetailersHomePageFooterInsiderLabelClick(),
        },
        {
          name: strictLocalize({
            id: "wQoOWp",
            defaultMessage: "POS integration",
            description: {
              text: "Link to the POS integrations marketing page",
              img: "https://cdn.faire.com/fastly/8b21891886b265c21e7e062892d2b9524ca29f98f7a80c90c0a877efe1ab405a.png",
            },
          }),
          to: posAbout(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
      ],
    },
  ];

  return (
    <Flex
      align="flex-start"
      direction={{ mobileAndAbove: "column", desktopAndAbove: "row" }}
      className="w-full py-8 md:mt-2 md:w-max md:pt-0 md:pb-8 md:pl-[50px] lg:mt-3 lg:pl-0 xl:mt-4"
    >
      <Row
        justify={{
          mobileAndAbove: "space-between",
          tabletAndAbove: "flex-start",
        }}
        className="w-full md:w-fit"
      >
        {footerSections.map((section) => (
          <Column
            key={section.title}
            className="min-w-[140px] gap-3 xl:w-[220px] 2xl:w-[280px]"
          >
            <Typography variant="paragraphSansMedium">
              {section.title}
            </Typography>
            {section.links.map((link) => (
              <SectionLink key={link.name} link={link} />
            ))}
          </Column>
        ))}
      </Row>
      <SocialLinks />
    </Flex>
  );
};

const SocialLinks: React.FC = () => {
  const onSocialLinkClick = (href: string) => {
    trackLoggedOutRetailersFooterLinksActionCategoryClick(href);
  };

  return (
    <Row
      justify={{ mobileAndAbove: "flex-end", tabletAndAbove: "flex-start" }}
      align="center"
      className="w-fit pt-8 lg:px-4 lg:pt-0 xl:pr-[100px] 2xl:pr-[150px]"
      gap="4x"
    >
      <AccessibilityAnchor
        href={SOCIAL.INSTAGRAM}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onSocialLinkClick(SOCIAL.INSTAGRAM)}
      >
        <SocialInstagram fill={Core.icon.primary} />
      </AccessibilityAnchor>
      <AccessibilityAnchor
        href={SOCIAL.FACEBOOK}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onSocialLinkClick(SOCIAL.FACEBOOK)}
      >
        <SocialFacebook fill={Core.icon.primary} />
      </AccessibilityAnchor>
      <AccessibilityAnchor
        href={SOCIAL.X}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onSocialLinkClick(SOCIAL.X)}
      >
        <SocialX className="mt-1" width="15px" height="15px" />
      </AccessibilityAnchor>
    </Row>
  );
};
